import React, { useMemo } from "react";
import { CurrencyDollar, Trophy } from "@phosphor-icons/react";

/**
 * Two-tier contest picker.
 *
 * Row 1 — STRATEGY (Cash vs GPP): dictates the core optimization goal
 *   (median-first vs ceiling-first).
 * Row 2 — FIELD & ENTRY FORMAT: subset of presets filtered by strategy,
 *   dictates uniqueness / variance / roster shape.
 *
 * Preset keys are the SAME as the backend `/api/contest/presets` map so
 * `applyPreset(key)` on the parent still works verbatim — this component
 * is purely presentational.
 */

const STRATEGIES = [
  {
    key: "cash",
    label: "Cash Games",
    tagline: "Floor-first · median-optimal",
    icon: CurrencyDollar,
    presetKeys: ["cash_50_50"],
  },
  {
    key: "gpp",
    label: "GPP · Tournaments",
    tagline: "Ceiling-first · leverage & uniqueness",
    icon: Trophy,
    presetKeys: [
      // Grouped inside the GPP tab by field/entry shape (see FIELD_GROUPS)
      "milly_maker", "20_max", "big_play_25", "flea_flicker",
      "three_max",
      "single_entry_150",
      "small_gpp",
      "custom",
    ],
  },
];

/** GPP field-shape groupings (labels inside the second row for GPP mode). */
const FIELD_GROUPS_GPP = [
  {
    header: "Massive Multi-Entry (150-Max / 20-Max)",
    hint: "Extreme variance · low-owned sleepers · deep player pool",
    keys: ["milly_maker", "20_max", "big_play_25", "flea_flicker"],
  },
  {
    header: "Small-Medium Multi-Entry",
    hint: "Smart stacks · avoid the bottom-of-barrel punt plays",
    keys: ["three_max"],
  },
  {
    header: "Single-Entry",
    hint: "Highly optimized core plays · selective correlation",
    keys: ["single_entry_150"],
  },
  {
    header: "Small Field (<5k entries)",
    hint: "Chalk-eating mode · you only need to outscore ~1% of the field",
    keys: ["small_gpp"],
  },
  {
    header: "Fully Manual",
    hint: "Every parameter tuned by you (Slate Intel → Custom)",
    keys: ["custom"],
  },
];

const FIELD_GROUPS_CASH = [
  {
    header: "50/50 & Double-Ups",
    hint: "Beat the median · floor > ceiling · chalk is safe",
    keys: ["cash_50_50"],
  },
];

/** Figure out which strategy the currently-selected preset belongs to. */
function strategyFor(presetKey) {
  const found = STRATEGIES.find((s) => s.presetKeys.includes(presetKey));
  return found?.key || "gpp";
}

export default function StrategyPicker({
  presets,          // full backend preset map from /api/contest/presets
  active,           // active preset key
  onPick,           // (presetKey) => void
  disabled = false,
  testIdPrefix = "strategy",
}) {
  const strategy = useMemo(() => strategyFor(active), [active]);
  const groups = strategy === "cash" ? FIELD_GROUPS_CASH : FIELD_GROUPS_GPP;

  // When user switches strategy, jump to the first available preset in that
  // strategy so we don't hold a stale preset that no longer matches.
  const onStrategyChange = (nextStrategy) => {
    if (disabled) return;
    const meta = STRATEGIES.find((s) => s.key === nextStrategy);
    const firstAvail = meta?.presetKeys.find((k) => presets?.[k]);
    if (firstAvail) onPick(firstAvail);
  };

  return (
    <div className="space-y-3">
      {/* Sprint 60.73 — Row 1 (Cash Games / GPP Tournaments toggle) removed.
          Redundant with the top-level MILLY / GPP / CASH / SHOWDOWN
          picker above; users kept asking why the same choice appeared
          twice. The preset description below is the only durable value
          this component still carries. */}

      {/* Row 2 — FIELD & ENTRY FORMAT (filtered) */}
      {/* Sprint 60.72 — Section removed. Redundant with the top-level
          MILLY / GPP / CASH / SHOWDOWN picker + the contest-preset row
          above. Users kept getting overwhelmed by having three
          contest-shape controls on the same page. Description of the
          active preset (below) stays. */}

      {/* Active preset description carried over from the old picker */}
      {presets?.[active]?.description && (
        <p className="text-xs text-zinc-400 leading-relaxed pt-2 border-t border-zinc-900">
          {presets[active].description}
        </p>
      )}
    </div>
  );
}
